/**
 * ThrowPreRun — headless evaluation of a local throw, run IN the live world.
 *
 * Phone Dice pattern (PhoneDiceWeb/modules/dice-engine-driver.js preRun):
 * before a throw plays visually, step the physics forward and check that
 * every die SETTLES (cannon sleep) on a VALID face within a frame budget.
 * Throws that wedge against a wall, balance on an edge, or slide forever
 * get rejected so the caller can re-randomize the throw and try again —
 * the player never watches a die creep for five seconds.
 *
 * Why the LIVE world and not a parallel one: simulating in the same world
 * the real throw will run in guarantees geometry / material / mass / solver
 * parity by construction (a parallel world silently drifts the first time
 * someone tunes a contact material in one place). Phone Dice does the same
 * (`this.world` with sound off). Every DYNAMIC body is snapshotted before
 * stepping and restored bit-exact after, and `world.time` is rewound so
 * cannon's sleep timers (relative to world time) are unaffected. The caller
 * runs this synchronously between fixed updates — the render loop never
 * sees the simulated frames.
 *
 * The verdict is intentionally STRICTER than the live pipeline (which can
 * rescue a leaner via nudgeToNearestFace / dampenSliding): a pre-run reject
 * costs one cheap re-randomize, while a live rescue costs seconds of
 * visible creeping. The live rescue logic stays as the safety net for
 * prediction misses (e.g. a die clipping a bill stack the snapshot put
 * back mid-flight).
 */

import * as CANNON from 'cannon-es';
import type { Dice } from './Dice';

export type PreRunReason = 'settled' | 'timeout' | 'leaning';

export interface PreRunResult {
  /** True when every die slept on a valid face within the budget. */
  ok: boolean;
  /** Simulated frames consumed (== budget on timeout). */
  frames: number;
  reason: PreRunReason;
}

interface BodySnapshot {
  body: CANNON.Body;
  position: CANNON.Vec3;
  quaternion: CANNON.Quaternion;
  velocity: CANNON.Vec3;
  angularVelocity: CANNON.Vec3;
  force: CANNON.Vec3;
  torque: CANNON.Vec3;
  previousPosition: CANNON.Vec3;
  previousQuaternion: CANNON.Quaternion;
  sleepState: CANNON.Body['sleepState'];
  timeLastSleepy: number;
  wakeUpAfterNarrowphase: boolean;
}

/**
 * Simulate the throw currently loaded into the dice bodies until every die
 * sleeps or `frameBudget` runs out, then restore the world exactly as it
 * was. `dt` must be the engine's fixed step (1/60) — any other value
 * changes contact resolution and invalidates the prediction.
 *
 * The caller is responsible for muting audio around this call (dice
 * 'collide' listeners fire during the simulated steps).
 */
export function preRunThrow(
  world: CANNON.World,
  dice: Dice[],
  frameBudget: number,
  dt: number,
): PreRunResult {
  // Snapshot every non-static body — the thrown dice plus anything else
  // the sim could disturb (bill stacks share the dice collision mask).
  const snapshots: BodySnapshot[] = [];
  for (const body of world.bodies) {
    if (body.type === CANNON.Body.STATIC) continue;
    snapshots.push({
      body,
      position: body.position.clone(),
      quaternion: body.quaternion.clone(),
      velocity: body.velocity.clone(),
      angularVelocity: body.angularVelocity.clone(),
      force: body.force.clone(),
      torque: body.torque.clone(),
      previousPosition: body.previousPosition.clone(),
      previousQuaternion: body.previousQuaternion.clone(),
      sleepState: body.sleepState,
      timeLastSleepy: body.timeLastSleepy,
      wakeUpAfterNarrowphase: body.wakeUpAfterNarrowphase,
    });
  }
  const timeBefore = world.time;

  let frames = 0;
  let result: PreRunResult | null = null;

  while (frames < frameBudget) {
    world.step(dt);
    frames++;

    let allSleeping = true;
    for (const die of dice) {
      if (die.body.sleepState !== CANNON.Body.SLEEPING) {
        allSleeping = false;
        break;
      }
    }
    if (!allSleeping) continue;

    // All asleep — leaning/edge rest is a reject (Phone Dice's invalid-face
    // check): the live pipeline would have to nudge it, and a nudge against
    // the wall is exactly the slow-creep loop this exists to prevent.
    const allValid = dice.every((die) => die.isOnValidFace());
    result = allValid
      ? { ok: true, frames, reason: 'settled' }
      : { ok: false, frames, reason: 'leaning' };
    break;
  }

  if (!result) result = { ok: false, frames, reason: 'timeout' };

  // Restore the world bit-exact. aabbNeedsUpdate forces broadphase to
  // recompute from the restored poses on the next real step.
  for (const s of snapshots) {
    s.body.position.copy(s.position);
    s.body.quaternion.copy(s.quaternion);
    s.body.velocity.copy(s.velocity);
    s.body.angularVelocity.copy(s.angularVelocity);
    s.body.force.copy(s.force);
    s.body.torque.copy(s.torque);
    s.body.previousPosition.copy(s.previousPosition);
    s.body.previousQuaternion.copy(s.previousQuaternion);
    s.body.sleepState = s.sleepState;
    s.body.timeLastSleepy = s.timeLastSleepy;
    s.body.wakeUpAfterNarrowphase = s.wakeUpAfterNarrowphase;
    s.body.aabbNeedsUpdate = true;
  }
  world.time = timeBefore;

  return result;
}
